import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import WelcomeHeader from './components/WelcomeHeader';
import DashboardStats from './components/DashboardStats';
import EnquiryCard from './components/EnquiryCard';
import RecentMessages from './components/RecentMessages';
import QuickActions from './components/QuickActions';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const CustomerDashboard = () => {
  const { user, userProfile } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    openEnquiries: 0,
    newMessages: 0,
    quotesReceived: 0,
    tripsBooked: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  // Load user-specific enquiries with stats from Supabase
  const loadEnquiries = useCallback(async () => {
    if (!user?.id) {
      console.log('[Dashboard] No user ID, skipping enquiries load');
      return;
    }

    console.log('[Dashboard] Loading enquiries for user:', user?.id);
    try {
      // Fetch enquiries
      const { data: enquiriesData, error: enquiriesError } = await supabase
        ?.from('enquiries')
        ?.select('*')
        ?.eq('customer_id', user?.id)
        ?.order('created_at', { ascending: false });

      if (enquiriesError) {
        console.error('[Dashboard] Enquiries load error:', enquiriesError);
        throw enquiriesError;
      }

      console.log('[Dashboard] Enquiries loaded successfully:', enquiriesData?.length || 0);

      // For each enquiry, fetch stats
      const enquiriesWithStats = await Promise.all(
        enquiriesData?.map(async (enquiry) => {
          // Count matched agents (conversations for this enquiry)
          const { count: conversationCount } = await supabase
            ?.from('conversations')
            ?.select('*', { count: 'exact', head: true })
            ?.eq('enquiry_id', enquiry?.id);

          // Get conversations to check for unread messages and latest message
          const { data: conversations } = await supabase
            ?.from('conversations')
            ?.select('id')
            ?.eq('enquiry_id', enquiry?.id);

          let unreadMessagesCount = 0;
          let lastMessageTime = null;

          if (conversations && conversations?.length > 0) {
            const conversationIds = conversations?.map(c => c?.id);

            // Count unread messages from agents in these conversations
            const { data: unreadMessages } = await supabase
              ?.from('messages')
              ?.select('id, created_at')
              ?.in('conversation_id', conversationIds)
              ?.eq('is_read', false)
              ?.neq('sender_id', user?.id);

            unreadMessagesCount = unreadMessages?.length || 0;

            // Get latest message timestamp for fallback
            const { data: latestMessage } = await supabase
              ?.from('messages')
              ?.select('created_at')
              ?.in('conversation_id', conversationIds)
              ?.order('created_at', { ascending: false })
              ?.limit(1)
              ?.single();

            lastMessageTime = latestMessage?.created_at;
          }

          // Get lowest quote price from conversations table
          const { data: quotedConversations } = await supabase
            ?.from('conversations')
            ?.select('quote_price, quote_currency')
            ?.eq('enquiry_id', enquiry?.id)
            ?.not('quote_price', 'is', null)
            ?.order('quote_price', { ascending: true })
            ?.limit(1);

          const lowestQuote = quotedConversations?.[0]?.quote_price || null;
          const quoteCurrency = quotedConversations?.[0]?.quote_currency || 'GBP';

          return {
            id: enquiry?.id,
            destination: enquiry?.destination,
            tripType: enquiry?.trip_type,
            startDate: enquiry?.departure_date,
            endDate: enquiry?.return_date,
            travelers: enquiry?.group_size,
            budget: enquiry?.budget_range?.max || 0,
            currency: 'GBP',
            accommodation: enquiry?.accommodation_preferences,
            duration: enquiry?.metadata?.duration || 0,
            status: enquiry?.status,
            matchedAgents: conversationCount || 0,
            unreadMessages: unreadMessagesCount,
            lastMessageTime: lastMessageTime,
            lowestQuote: lowestQuote,
            quoteCurrency: quoteCurrency,
            requirements: enquiry?.description,
            createdAt: enquiry?.created_at
          };
        }) || []
      );

      setEnquiries(enquiriesWithStats);
    } catch (error) {
      console.error('[Dashboard] Error loading enquiries:', error);
      setEnquiries([]);
      throw error;
    }
  }, [user?.id]);

  // Load user-specific messages from Supabase
  const loadRecentMessages = useCallback(async () => {
    if (!user?.id) {
      console.log('[Dashboard] No user ID, skipping messages load');
      return;
    }

    console.log('[Dashboard] Loading recent messages for user:', user?.id);
    try {
      const { data, error } = await supabase
        ?.from('messages')
        ?.select(`
          *,
          conversation:conversations(
            *,
            enquiry:enquiries(destination)
          ),
          sender:user_profiles!messages_sender_id_fkey(full_name, avatar_url)
        `)
        ?.neq('sender_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (error) {
        console.error('[Dashboard] Messages load error:', error);
        throw error;
      }

      console.log('[Dashboard] Messages loaded successfully:', data?.length || 0);

      // Transform to UI format
      const transformedMessages = data?.map(msg => ({
        id: msg?.id,
        enquiryId: msg?.conversation?.enquiry_id,
        enquiryDestination: msg?.conversation?.enquiry?.destination || 'Unknown',
        agentName: msg?.sender?.full_name || 'Agent',
        agentAvatar: msg?.sender?.avatar_url || '',
        agentAvatarAlt: `Profile picture of ${msg?.sender?.full_name || 'Agent'}`,
        lastMessage: msg?.content,
        timestamp: new Date(msg?.created_at),
        unread: !msg?.is_read,
        hasAttachment: !!msg?.file_url
      }));

      setRecentMessages(transformedMessages || []);
    } catch (error) {
      console.error('[Dashboard] Error loading messages:', error);
      setRecentMessages([]);
      throw error;
    }
  }, [user?.id]);

  // Calculate dashboard statistics from real data
  const calculateStats = useCallback(async () => {
    if (!user?.id) {
      console.log('[Dashboard] No user ID, skipping stats calculation');
      return;
    }

    console.log('[Dashboard] Calculating statistics for user:', user?.id);
    try {
      // Count open enquiries (use 'pending' status, not 'open')
      console.log('[Dashboard] Counting open enquiries...');
      const { count: openEnquiryCount, error: openEnquiryError } = await supabase
        ?.from('enquiries')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('customer_id', user?.id)
        ?.eq('status', 'proposed');

      if (openEnquiryError) {
        console.error('[Dashboard] Open enquiry count error:', openEnquiryError);
        // Don't throw - continue with 0 count
      }
      console.log('[Dashboard] Open enquiries:', openEnquiryCount || 0);

      // Count unread messages (new messages) - Fixed query
      console.log('[Dashboard] Counting new messages...');
      
      // First get all conversation IDs for this customer
      const { data: customerConversations, error: conversationsError } = await supabase
        ?.from('conversations')
        ?.select('id')
        ?.eq('customer_id', user?.id);

      if (conversationsError) {
        console.error('[Dashboard] Conversations fetch error:', conversationsError);
        // Don't throw - continue with 0 count
      }

      let newMessagesCount = 0;
      if (customerConversations && customerConversations?.length > 0) {
        const conversationIds = customerConversations?.map(c => c?.id);
        
        // Now count unread messages in those conversations
        const { count: unreadCount, error: unreadError } = await supabase
          ?.from('messages')
          ?.select('*', { count: 'exact', head: true })
          ?.in('conversation_id', conversationIds)
          ?.eq('is_read', false)
          ?.neq('sender_id', user?.id);

        if (unreadError) {
          console.error('[Dashboard] New messages count error:', unreadError);
          // Don't throw - continue with 0 count
        }
        
        newMessagesCount = unreadCount || 0;
      }
      
      console.log('[Dashboard] New messages:', newMessagesCount);

      // Count quotes received (conversations with quote_price not null)
      console.log('[Dashboard] Counting quotes received...');
      const { count: quotesCount, error: quotesError } = await supabase
        ?.from('conversations')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('customer_id', user?.id)
        ?.not('quote_price', 'is', null);

      if (quotesError) {
        console.error('[Dashboard] Quotes count error:', quotesError);
        // Don't throw - continue with 0 count
      }
      console.log('[Dashboard] Quotes received:', quotesCount || 0);

      // Count trips booked (completed enquiries)
      console.log('[Dashboard] Counting trips booked...');
      const { count: tripsBookedCount, error: tripsBookedError } = await supabase
        ?.from('enquiries')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('customer_id', user?.id)
        ?.eq('status', 'completed');

      if (tripsBookedError) {
        console.error('[Dashboard] Trips booked count error:', tripsBookedError);
        // Don't throw - continue with 0 count
      }
      console.log('[Dashboard] Trips booked:', tripsBookedCount || 0);

      setDashboardStats({
        openEnquiries: openEnquiryCount || 0,
        newMessages: newMessagesCount,
        quotesReceived: quotesCount || 0,
        tripsBooked: tripsBookedCount || 0
      });
      
      console.log('[Dashboard] Statistics calculated successfully');
    } catch (error) {
      console.error('[Dashboard] Error calculating stats:', error);
      // Don't throw - allow dashboard to load with default stats
      setDashboardStats({
        openEnquiries: 0,
        newMessages: 0,
        quotesReceived: 0,
        tripsBooked: 0
      });
    }
  }, [user?.id]);

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user?.id) {
        console.log('[Dashboard] Waiting for user authentication...');
        setLoading(false);
        return;
      }
      
      console.log('[Dashboard] Starting dashboard data load...');
      setLoading(true);
      setError(null);
      
      try {
        await Promise.all([
          loadEnquiries(),
          loadRecentMessages(),
          calculateStats()
        ]);
        console.log('[Dashboard] All data loaded successfully');
        
        // ✅ Check for enquiry creation success message
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams?.get('enquiry_created') === 'true') {
          console.log('[Dashboard] New enquiry created - showing success message');
          // Clean URL
          window.history?.replaceState({}, '', '/customer-dashboard');
        }
      } catch (err) {
        console.error('[Dashboard] Dashboard load failed:', err);
        setError({
          message: err?.message || 'Failed to load dashboard data',
          code: err?.code,
          details: err?.details
        });
      } finally {
        setLoading(false);
        console.log('[Dashboard] Loading complete');
      }
    };

    loadDashboardData();
  }, [user?.id, loadEnquiries, loadRecentMessages, calculateStats]);

  const handleCreateEnquiry = () => {
    window.location.href = '/create-enquiry';
  };

  const handleViewMessages = (enquiryId = null) => {
    const url = enquiryId 
      ? `/customer/messages-v2/enquiry/${enquiryId}` 
      : '/customer/messages-v2';
    window.location.href = url;
  };

  const handleEditEnquiry = (enquiryId) => {
    window.location.href = `/create-enquiry?edit=${enquiryId}`;
  };

  const handleCloseEnquiry = (enquiryId) => {
    setEnquiries((prev) =>
    prev?.map((enquiry) =>
    enquiry?.id === enquiryId ?
    { ...enquiry, status: 'closed' } :
    enquiry
    )
    );
  };

  const handleViewProfile = () => {
    window.location.href = '/customer-account-settings';
  };

  const handleLogout = () => {
    localStorage.removeItem('holiyay_user');
    window.location.href = '/';
  };

  const handleQuotesReceivedClick = () => {
    setFilterStatus('quote_received');
  };

  const handleRefreshDashboard = async () => {
    console.log('[Dashboard] Manual refresh triggered');
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        loadEnquiries(),
        loadRecentMessages(),
        calculateStats()
      ]);
      console.log('[Dashboard] Refresh successful');
    } catch (err) {
      console.error('[Dashboard] Refresh failed:', err);
      setError({
        message: err?.message || 'Failed to refresh dashboard',
        code: err?.code,
        details: err?.details
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredEnquiries = enquiries?.filter((enquiry) => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'quote_received') {
      // Only show enquiries that have actual quotes (lowestQuote is not null)
      return enquiry?.lowestQuote !== null;
    }
    if (filterStatus === 'awaiting_response') {
      // Show enquiries with matched agents but no quotes yet
      return enquiry?.matchedAgents > 0 && enquiry?.lowestQuote === null;
    }
    if (filterStatus === 'completed') {
      return enquiry?.status === 'completed';
    }
    return enquiry?.status === filterStatus;
  });

  const statusFilters = [
    { key: 'all', label: 'All Enquiries', count: enquiries?.length },
    { 
      key: 'quote_received', 
      label: 'Quote Received', 
      count: enquiries?.filter((e) => e?.lowestQuote !== null)?.length 
    },
    { 
      key: 'awaiting_response', 
      label: 'Awaiting Response', 
      count: enquiries?.filter((e) => e?.matchedAgents > 0 && e?.lowestQuote === null)?.length 
    },
    { 
      key: 'completed', 
      label: 'Completed', 
      count: enquiries?.filter((e) => e?.status === 'completed')?.length 
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="customer" onLogout={handleLogout} />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state UI
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="customer" onLogout={handleLogout} />
        <div className="pt-16 min-h-screen flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-card border border-destructive rounded-lg p-6 text-center">
            <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Unable to Load Dashboard
            </h2>
            <p className="text-muted-foreground mb-4">
              {error?.message}
            </p>
            {error?.code && (
              <p className="text-sm text-muted-foreground mb-4">
                Error Code: {error?.code}
              </p>
            )}
            {error?.details && (
              <details className="text-left mb-4">
                <summary className="text-sm text-muted-foreground cursor-pointer">
                  Technical Details
                </summary>
                <pre className="text-xs bg-muted p-2 rounded mt-2 overflow-auto">
                  {error?.details}
                </pre>
              </details>
            )}
            <div className="flex gap-3 justify-center">
              <Button
                variant="default"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={handleRefreshDashboard}
              >
                Refresh Page
              </Button>
              <Button
                variant="outline"
                iconName="MessageSquare"
                iconPosition="left"
                onClick={() => window.location.href = '/messages'}
              >
                Go to Messages
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        userRole="customer"
        notificationCount={recentMessages?.filter((m) => m?.unread)?.length}
        onLogout={handleLogout} />

      <main className="pt-16 px-4 lg:px-6 py-6 max-w-7xl mx-auto">
        {/* Welcome Header */}
        <WelcomeHeader userName={userProfile?.full_name || user?.email?.split('@')?.[0]} currentTime={new Date()} />

        {/* Dashboard Statistics */}
        <DashboardStats stats={dashboardStats} onQuotesReceivedClick={handleQuotesReceivedClick} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Enquiries */}
          <div className="lg:col-span-2 space-y-6" data-enquiries-section>
            {/* Enquiries Header with Global Messages Button */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Your Enquiries</h2>
                <p className="text-sm text-muted-foreground">
                  Manage your holiday enquiries and connect with travel agents
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  iconName="MessageCircle"
                  iconPosition="left"
                  onClick={() => handleViewMessages()}
                  className="border-primary text-primary hover:bg-primary/10">
                  Messages
                </Button>
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleCreateEnquiry}
                  className="bg-accent hover:bg-accent/90 text-accent-foreground">
                  Create New Enquiry
                </Button>
              </div>
            </div>

            {/* Status Filter Tabs */}
            <div className="flex flex-wrap gap-2 border-b border-border">
              {statusFilters?.map((filter) =>
              <button
                key={filter?.key}
                onClick={() => setFilterStatus(filter?.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                filterStatus === filter?.key ?
                'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
                }>

                  {filter?.label}
                  {filter?.count > 0 &&
                <span className="ml-2 px-2 py-1 text-xs bg-muted text-muted-foreground rounded-full">
                      {filter?.count}
                    </span>
                }
                </button>
              )}
            </div>

            {/* Enquiries List */}
            <div className="space-y-4">
              {filteredEnquiries?.length > 0 ?
              filteredEnquiries?.map((enquiry) =>
              <EnquiryCard
                key={enquiry?.id}
                enquiry={enquiry}
                onViewMessages={handleViewMessages}
                onEditEnquiry={handleEditEnquiry}
                onCloseEnquiry={handleCloseEnquiry} />

              ) :

              <div className="text-center py-12 bg-card border border-border rounded-lg">
                  <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    {filterStatus === 'all' ? 'No enquiries yet' : `No ${filterStatus?.replace('_', ' ')} enquiries`}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {filterStatus === 'all' ? 'Start planning your dream holiday by creating your first enquiry' : 'Try adjusting your filter or create a new enquiry'}
                  </p>
                  <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleCreateEnquiry}>

                    Create Your First Enquiry
                  </Button>
                </div>
              }
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Recent Messages */}
            <RecentMessages
              messages={recentMessages}
              onViewAllMessages={handleViewMessages} />


            {/* Quick Actions */}
            <QuickActions
              onCreateEnquiry={handleCreateEnquiry}
              onViewMessages={() => handleViewMessages()}
              onViewProfile={handleViewProfile} />

          </div>
        </div>
      </main>
    </div>
  );
};

export default CustomerDashboard;